import {
  findApplication,
  normalizeOrigin,
  type ApplicationRegistry,
  type ProductKey,
} from '@traceio-org/platform-contracts';
import type { HostState } from './host';

export interface DestinationPolicy {
  /** The registry from GET /v1/applications, or null when it could not be loaded. */
  registry: ApplicationRegistry | null;
  /** The address the hub is served on (a verified custom domain counts as a destination). */
  host: HostState;
}

export type DestinationRejection =
  | 'invalid_url'
  | 'scheme'
  | 'credentials'
  | 'origin_not_allowed'
  | 'registry_unavailable';

export type DestinationCheck = { ok: true; url: string } | { ok: false; reason: DestinationRejection };

function isLocalDevelopmentHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

/**
 * Defense in depth for redirect decisions. platform-api builds the URL from its registry, and the hub
 * still refuses to leave unless the URL's origin is exactly:
 *   1. the registered destination origin of the SAME product in this environment, or
 *   2. the verified custom domain the hub is served on, when that domain belongs to the same product.
 * Custom domains must use https on the default port; plain http and ports are accepted only for local
 * *.localhost hosts. Everything else (another product's origin, look-alike hosts, other schemes, URLs with
 * credentials) is refused, and so is every redirect when the registry is unavailable (fail closed).
 */
export function checkDestination(rawUrl: string, productKey: ProductKey, policy: DestinationPolicy): DestinationCheck {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return { ok: false, reason: 'invalid_url' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return { ok: false, reason: 'scheme' };
  if (url.username || url.password) return { ok: false, reason: 'credentials' };

  const host = policy.host;
  if (
    (host.kind === 'customer-employee' || host.kind === 'customer-portal') &&
    host.forcedProduct === productKey &&
    url.hostname === host.host
  ) {
    const local = isLocalDevelopmentHost(url.hostname);
    const schemeOk = url.protocol === 'https:' || local;
    const portOk = url.port === '' || local;
    if (schemeOk && portOk) return { ok: true, url: url.toString() };
  }

  if (!policy.registry) return { ok: false, reason: 'registry_unavailable' };
  const destination = findApplication(policy.registry, productKey)?.destination;
  if (!destination) return { ok: false, reason: 'origin_not_allowed' };
  let registered: string;
  try {
    registered = normalizeOrigin(destination.origin);
  } catch {
    return { ok: false, reason: 'origin_not_allowed' };
  }
  return url.origin === registered ? { ok: true, url: url.toString() } : { ok: false, reason: 'origin_not_allowed' };
}
